<template>
  <div
    class="mt-4 flex h-full flex-col justify-between space-y-2"
  >
    <IconEnvelope
      class="size-40 self-center text-background-fg/70"
    />

    <div
      v-if="errorMessage !== null"
      class="text-center text-error"
    >
      {{ errorMessage }}
    </div>
    <div
      v-else-if="
        errorMessage === null &&
        commsMethod !== null &&
        commsRecipient !== null
      "
      class="text-center font-bold"
    >
      We've
      {{ commsMethod === "email" ? "emailed" : "texted" }}
      a link to {{ commsRecipient }}
    </div>
    <div v-if="errorMessage === null" class="space-y-2">
      <div class="text-center text-sm">
        Click on the link to approve this device
      </div>
      <ActionButtonSecondary
        class="w-full"
        @click="attemptCommsSend('email')"
        >Email link again</ActionButtonSecondary
      >
      <ActionButtonSecondary
        v-if="organizationConfig?.smsServiceEnabled"
        class="w-full"
        @click="attemptCommsSend('text')"
        >Text link</ActionButtonSecondary
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { ApiError } from "@/services/api"
import { sendConfirmUserComms } from "@/services/onboarding/sendConfirmUserComms"
import { checkUserConfirmed } from "@/services/onboarding/checkUserConfirmed"

import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"
type Emits = {
  (
    eventName: "stage-complete",
    merchantName: string | null
  ): void
}

const emit = defineEmits<Emits>()

const { organizationConfig } = useOrganizationConfig()

const { startLoading, stopLoading } =
  usePageBlockingLoading()

const errorMessage = ref<string | null>(null)
const commsMethod = ref<"email" | "text" | null>(null)
const commsRecipient = ref<string | null>(null)

const checkStageCompleteIntervalId = ref<number | null>(
  null
)

const attemptCommsSend = async (
  method: "text" | "email"
) => {
  startLoading()
  errorMessage.value = null

  try {
    const response = await sendConfirmUserComms({ method })
    commsMethod.value = response.method
    commsRecipient.value = response.recipient
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.code === "user-already-confirmed"
    ) {
      emit("stage-complete", null)
    } else if (
      error instanceof ApiError &&
      error.code === "cant-find-contact-details"
    ) {
      const orgNameFallback = "takepayments"

      errorMessage.value = `Unable to determine your contact details, please contact ${
        organizationConfig.value?.organizationName ??
        orgNameFallback
      } for help`
    } else {
      toast.error("Failed to send activation link")
    }
  } finally {
    stopLoading()
  }
}

const checkStageComplete = async () => {
  try {
    const response = await checkUserConfirmed()

    if (response.userIsConfirmed) {
      emit("stage-complete", response.merchantName)
    }
  } catch {
    // Do nothing
  }
}

onMounted(async () => {
  await attemptCommsSend("email")
  checkStageCompleteIntervalId.value = window.setInterval(
    checkStageComplete,
    5000
  )
})

onUnmounted(() => {
  if (checkStageCompleteIntervalId.value !== null) {
    clearInterval(checkStageCompleteIntervalId.value)
  }
})
</script>
